'use client';
import React, { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

// ─── Types ────────────────────────────────────────────────────────────────────
type StreakTier = 'beginner' | 'consistent' | 'dedicated' | 'champion';
type ConnectionStatus = 'none' | 'pending' | 'connected';

interface MemberProfile {
  id: string;
  display_name: string;
  avatar_initials: string;
  current_streak: number;
  longest_streak: number;
  prayers_completed: number;
  top_habits: string[];
  prayer_groups: string[];
  streak_tier: StreakTier;
  connection_status: ConnectionStatus;
  is_online: boolean;
  location?: string;
  joined_date: string;
  weekly_streak_history: number[];
  monthly_streak_history: { month: string; days: number; total: number }[];
  achievements: Achievement[];
  bio?: string;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  emoji: string;
  earned_at: string;
  category: 'streak' | 'prayer' | 'habit' | 'community';
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const MOCK_PROFILES: Record<string, MemberProfile> = {
  u1: {
    id: 'u1', display_name: 'Fatima Al-Rashid', avatar_initials: 'FA',
    current_streak: 142, longest_streak: 142, prayers_completed: 710,
    top_habits: ['Fajr on time', 'Quran daily', 'Dhikr morning', 'Charity weekly', 'Night prayer'],
    prayer_groups: ['Fajr Warriors', 'Quran Circle'],
    streak_tier: 'champion', connection_status: 'none', is_online: true, location: 'Riyadh',
    joined_date: 'January 2024',
    bio: 'Striving to be consistent in worship and helping others on their spiritual journey.',
    weekly_streak_history: [5, 5, 5, 5, 5, 5, 5],
    monthly_streak_history: [
      { month: 'Jan', days: 28, total: 31 }, { month: 'Feb', days: 26, total: 28 },
      { month: 'Mar', days: 31, total: 31 }, { month: 'Apr', days: 30, total: 30 },
      { month: 'May', days: 31, total: 31 }, { month: 'Jun', days: 29, total: 30 },
    ],
    achievements: [
      { id: 'a1', title: '100-Day Champion', description: 'Maintained a 100-day prayer streak', emoji: '👑', earned_at: 'Mar 2024', category: 'streak' },
      { id: 'a2', title: 'Fajr Master', description: 'Never missed Fajr for 60 days', emoji: '🌅', earned_at: 'Feb 2024', category: 'prayer' },
      { id: 'a3', title: 'Quran Devotee', description: 'Read Quran daily for 30 days', emoji: '📖', earned_at: 'Jan 2024', category: 'habit' },
      { id: 'a4', title: 'Community Pillar', description: 'Connected with 10+ community members', emoji: '🤝', earned_at: 'Apr 2024', category: 'community' },
      { id: 'a5', title: 'Consistency King', description: 'Completed all 5 prayers for 30 days', emoji: '⭐', earned_at: 'Jan 2024', category: 'prayer' },
    ],
  },
  u2: {
    id: 'u2', display_name: 'Omar Hussain', avatar_initials: 'OH',
    current_streak: 98, longest_streak: 120, prayers_completed: 490,
    top_habits: ['5 prayers on time', 'Night prayer', 'Fasting Mondays', 'Istighfar daily'],
    prayer_groups: ['Night Prayers', 'Ramadan Prep'],
    streak_tier: 'champion', connection_status: 'connected', is_online: false, location: 'Cairo',
    joined_date: 'March 2024',
    bio: 'Seeking closeness to Allah through consistent prayer and night worship.',
    weekly_streak_history: [5, 4, 5, 5, 5, 4, 5],
    monthly_streak_history: [
      { month: 'Jan', days: 25, total: 31 }, { month: 'Feb', days: 24, total: 28 },
      { month: 'Mar', days: 29, total: 31 }, { month: 'Apr', days: 28, total: 30 },
      { month: 'May', days: 30, total: 31 }, { month: 'Jun', days: 27, total: 30 },
    ],
    achievements: [
      { id: 'a1', title: '100-Day Champion', description: 'Maintained a 100-day prayer streak', emoji: '👑', earned_at: 'May 2024', category: 'streak' },
      { id: 'a2', title: 'Night Warrior', description: 'Prayed Tahajjud for 30 nights', emoji: '🌙', earned_at: 'Apr 2024', category: 'prayer' },
      { id: 'a3', title: 'Ramadan Ready', description: 'Completed Ramadan preparation program', emoji: '🌙', earned_at: 'Mar 2024', category: 'habit' },
    ],
  },
};

const DEFAULT_PROFILE = (id: string): MemberProfile => ({
  id, display_name: 'Community Member', avatar_initials: 'CM',
  current_streak: 45, longest_streak: 60, prayers_completed: 225,
  top_habits: ['5 prayers on time', 'Quran daily'],
  prayer_groups: ['Fajr Warriors'],
  streak_tier: 'consistent', connection_status: 'none', is_online: false, location: 'Unknown',
  joined_date: 'June 2024',
  bio: 'A dedicated member of the Rafeeq community.',
  weekly_streak_history: [4, 5, 3, 5, 4, 5, 5],
  monthly_streak_history: [
    { month: 'Jan', days: 20, total: 31 }, { month: 'Feb', days: 22, total: 28 },
    { month: 'Mar', days: 25, total: 31 }, { month: 'Apr', days: 24, total: 30 },
    { month: 'May', days: 28, total: 31 }, { month: 'Jun', days: 26, total: 30 },
  ],
  achievements: [
    { id: 'a1', title: 'First Streak', description: 'Completed your first 7-day streak', emoji: '🔥', earned_at: 'Jun 2024', category: 'streak' },
    { id: 'a2', title: 'Prayer Starter', description: 'Logged 100 prayers', emoji: '🕌', earned_at: 'Jun 2024', category: 'prayer' },
  ],
});

// ─── Tier Config ──────────────────────────────────────────────────────────────
const TIER_CONFIG: Record<StreakTier, { label: string; emoji: string; color: string; bg: string; border: string }> = {
  beginner:   { label: 'Beginner',   emoji: '🌱', color: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  consistent: { label: 'Consistent', emoji: '🔥', color: 'text-orange-700',  bg: 'bg-orange-50',   border: 'border-orange-200' },
  dedicated:  { label: 'Dedicated',  emoji: '⭐', color: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200' },
  champion:   { label: 'Champion',   emoji: '👑', color: 'text-purple-700',  bg: 'bg-purple-50',   border: 'border-purple-200' },
};

const ACHIEVEMENT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  streak:    { bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200' },
  prayer:    { bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200' },
  habit:     { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200' },
  community: { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200' },
};

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MemberProfilePage() {
  const params = useParams();
  const router = useRouter();
  const { user } = useAuth();
  const supabase = createClient();
  const memberId = params?.id as string;

  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('none');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [activeSection, setActiveSection] = useState<'overview' | 'streaks' | 'achievements'>('overview');

  useEffect(() => {
    loadProfile();
  }, [memberId]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      // Try to load from Supabase
      const { data } = await supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .eq('id', memberId)
        .single();

      if (data) {
        const initials = (data.full_name || data.email || 'U')
          .split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
        const streak = Math.floor(Math.random() * 150);
        let tier: StreakTier = 'beginner';
        if (streak >= 100) tier = 'champion';
        else if (streak >= 60) tier = 'dedicated';
        else if (streak >= 30) tier = 'consistent';

        const base = DEFAULT_PROFILE(memberId);
        setProfile({
          ...base,
          id: data.id,
          display_name: data.full_name || data.email?.split('@')[0] || 'User',
          avatar_initials: initials,
          current_streak: streak,
          longest_streak: streak + Math.floor(Math.random() * 20),
          prayers_completed: streak * 5,
          streak_tier: tier,
        });
      } else {
        // Use mock data
        setProfile(MOCK_PROFILES[memberId] || DEFAULT_PROFILE(memberId));
      }
    } catch {
      setProfile(MOCK_PROFILES[memberId] || DEFAULT_PROFILE(memberId));
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!profile || connectionStatus !== 'none') return;
    setConnecting(true);
    await new Promise((r) => setTimeout(r, 600));
    setConnectionStatus('pending');
    setConnecting(false);
  };

  const handleMessage = () => {
    router.push('/messages');
  };

  if (loading) {
    return (
      <AppLayout activeRoute="/community">
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4" />
          <p className="text-muted-foreground text-sm">Loading profile…</p>
        </div>
      </AppLayout>
    );
  }

  if (!profile) return null;

  const tierCfg = TIER_CONFIG[profile.streak_tier];
  const status = connectionStatus !== 'none' ? connectionStatus : profile.connection_status;

  return (
    <AppLayout activeRoute="/community">
      {/* Back nav */}
      <div className="mb-5">
        <Link
          href="/community"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
        >
          <Icon name="ArrowLeftIcon" size={16} />
          Back to Community
        </Link>
      </div>

      {/* Profile Hero */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-5">
        <div className="flex items-start gap-5">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 rounded-2xl gradient-teal flex items-center justify-center text-white font-700 text-2xl">
              {profile.avatar_initials}
            </div>
            {profile.is_online && (
              <span className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-card" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h1 className="text-xl font-700 text-foreground">{profile.display_name}</h1>
              <span className={`text-xs px-2.5 py-1 rounded-full border font-600 ${tierCfg.bg} ${tierCfg.color} ${tierCfg.border}`}>
                {tierCfg.emoji} {tierCfg.label}
              </span>
              {profile.is_online && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-500">
                  Online
                </span>
              )}
            </div>
            {profile.location && (
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mb-1">
                <Icon name="MapPinIcon" size={13} />
                {profile.location}
              </p>
            )}
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-3">
              <Icon name="CalendarIcon" size={13} />
              Member since {profile.joined_date}
            </p>
            {profile.bio && (
              <p className="text-sm text-muted-foreground italic">&ldquo;{profile.bio}&rdquo;</p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 mt-5">
          <button
            onClick={handleConnect}
            disabled={status !== 'none' || connecting}
            className={`flex-1 py-2.5 rounded-xl text-sm font-600 transition-all duration-150 flex items-center justify-center gap-2 ${
              status === 'connected' ?'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default'
                : status === 'pending' ?'bg-muted text-muted-foreground border border-border cursor-default' :'bg-primary text-white hover:bg-primary/90'
            }`}
          >
            {connecting ? (
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
            ) : (
              <Icon
                name={status === 'connected' ? 'CheckCircleIcon' : status === 'pending' ? 'ClockIcon' : 'UserPlusIcon'}
                size={16}
              />
            )}
            {status === 'connected' ? 'Connected' : status === 'pending' ? 'Request Sent' : 'Connect'}
          </button>
          <button
            onClick={handleMessage}
            className="flex-1 py-2.5 rounded-xl text-sm font-600 bg-card border border-border text-foreground hover:bg-muted transition-all duration-150 flex items-center justify-center gap-2"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} />
            Message
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-card border border-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-700 text-foreground">{profile.current_streak}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Current Streak</p>
          <p className="text-xs text-orange-500 font-500 mt-1">🔥 days</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-700 text-foreground">{profile.longest_streak}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Best Streak</p>
          <p className="text-xs text-amber-500 font-500 mt-1">⭐ record</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4 text-center">
          <p className="text-2xl font-700 text-foreground">{profile.prayers_completed}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Prayers Logged</p>
          <p className="text-xs text-teal-500 font-500 mt-1">🕌 total</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-5">
        {([
          { key: 'overview', label: 'Overview', icon: 'UserCircleIcon' },
          { key: 'streaks', label: 'Streak History', icon: 'FireIcon' },
          { key: 'achievements', label: 'Achievements', icon: 'TrophyIcon' },
        ] as { key: typeof activeSection; label: string; icon: string }[]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveSection(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-500 transition-all duration-150 ${
              activeSection === tab.key
                ? 'bg-primary text-white shadow-sm'
                : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Overview Section ── */}
      {activeSection === 'overview' && (
        <div className="space-y-4">
          {/* Habits */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="font-600 text-foreground mb-3 flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={18} className="text-primary" />
              Active Habits
            </h2>
            <div className="flex flex-wrap gap-2">
              {profile.top_habits.map((habit) => (
                <span
                  key={habit}
                  className="text-sm bg-primary/8 text-primary px-3 py-1.5 rounded-full border border-primary/20 font-500"
                >
                  {habit}
                </span>
              ))}
            </div>
          </div>

          {/* Prayer Groups */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-600 text-foreground flex items-center gap-2">
                <Icon name="UserGroupIcon" size={18} className="text-primary" />
                Prayer Groups
              </h2>
              <Link href="/prayer-groups" className="text-xs text-primary hover:underline flex items-center gap-1">
                Browse all <Icon name="ArrowRightIcon" size={12} />
              </Link>
            </div>
            {profile.prayer_groups.length > 0 ? (
              <div className="space-y-2">
                {profile.prayer_groups.map((group) => (
                  <div key={group} className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="UserGroupIcon" size={15} className="text-primary" />
                    </div>
                    <span className="text-sm font-500 text-foreground">{group}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not in any prayer groups yet.</p>
            )}
          </div>

          {/* Weekly Activity */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="font-600 text-foreground mb-4 flex items-center gap-2">
              <Icon name="CalendarDaysIcon" size={18} className="text-primary" />
              This Week&apos;s Activity
            </h2>
            <div className="flex items-end justify-between gap-2">
              {profile.weekly_streak_history.map((count, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5 flex-1">
                  <div className="w-full flex flex-col gap-0.5">
                    {[5, 4, 3, 2, 1].map((level) => (
                      <div
                        key={level}
                        className={`h-3 rounded-sm transition-colors ${
                          count >= level ? 'bg-primary' : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground">{DAYS[i]}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3 text-center">
              {profile.weekly_streak_history.reduce((a, b) => a + b, 0)} prayers this week
            </p>
          </div>
        </div>
      )}

      {/* ── Streak History Section ── */}
      {activeSection === 'streaks' && (
        <div className="space-y-4">
          {/* Streak summary */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="font-600 text-foreground mb-4 flex items-center gap-2">
              <Icon name="FireIcon" size={18} className="text-orange-500" />
              Streak Summary
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-2xl font-700 text-orange-700">{profile.current_streak}</p>
                <p className="text-xs text-orange-600 font-500 mt-0.5">Current Streak</p>
                <p className="text-xs text-orange-500 mt-1">🔥 Keep it going!</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-2xl font-700 text-amber-700">{profile.longest_streak}</p>
                <p className="text-xs text-amber-600 font-500 mt-0.5">Personal Best</p>
                <p className="text-xs text-amber-500 mt-1">⭐ All-time record</p>
              </div>
            </div>
          </div>

          {/* Monthly history */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="font-600 text-foreground mb-4 flex items-center gap-2">
              <Icon name="ChartBarIcon" size={18} className="text-primary" />
              Monthly Consistency
            </h2>
            <div className="space-y-3">
              {profile.monthly_streak_history.map((month) => {
                const pct = Math.round((month.days / month.total) * 100);
                return (
                  <div key={month.month}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-500 text-foreground">{month.month}</span>
                      <span className="text-xs text-muted-foreground">
                        {month.days}/{month.total} days · <span className="text-primary font-600">{pct}%</span>
                      </span>
                    </div>
                    <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Streak milestones */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="font-600 text-foreground mb-4 flex items-center gap-2">
              <Icon name="FlagIcon" size={18} className="text-primary" />
              Streak Milestones
            </h2>
            <div className="space-y-2">
              {[
                { days: 7, label: '1 Week Warrior', emoji: '🌱' },
                { days: 30, label: '30-Day Consistent', emoji: '🔥' },
                { days: 60, label: '60-Day Dedicated', emoji: '⭐' },
                { days: 100, label: '100-Day Champion', emoji: '👑' },
              ].map((milestone) => {
                const reached = profile.longest_streak >= milestone.days;
                return (
                  <div
                    key={milestone.days}
                    className={`flex items-center gap-3 p-3 rounded-xl border ${
                      reached ? 'bg-primary/5 border-primary/20' : 'bg-muted border-border opacity-50'
                    }`}
                  >
                    <span className="text-xl">{milestone.emoji}</span>
                    <div className="flex-1">
                      <p className={`text-sm font-600 ${reached ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {milestone.label}
                      </p>
                      <p className="text-xs text-muted-foreground">{milestone.days} days streak</p>
                    </div>
                    {reached && (
                      <Icon name="CheckCircleIcon" size={18} className="text-primary flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Achievements Section ── */}
      {activeSection === 'achievements' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-600 text-foreground flex items-center gap-2">
                <Icon name="TrophyIcon" size={18} className="text-amber-500" />
                Achievements
              </h2>
              <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-full font-600">
                {profile.achievements.length} earned
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {profile.achievements.map((ach) => {
                const colors = ACHIEVEMENT_COLORS[ach.category];
                return (
                  <div
                    key={ach.id}
                    className={`flex items-start gap-3 p-4 rounded-xl border ${colors.bg} ${colors.border}`}
                  >
                    <span className="text-2xl flex-shrink-0">{ach.emoji}</span>
                    <div className="min-w-0">
                      <p className={`text-sm font-600 ${colors.text}`}>{ach.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{ach.description}</p>
                      <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                        <Icon name="CalendarIcon" size={11} />
                        {ach.earned_at}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Category breakdown */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <h2 className="font-600 text-foreground mb-4 flex items-center gap-2">
              <Icon name="ChartPieIcon" size={18} className="text-primary" />
              Achievement Categories
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {(['streak', 'prayer', 'habit', 'community'] as const).map((cat) => {
                const count = profile.achievements.filter((a) => a.category === cat).length;
                const colors = ACHIEVEMENT_COLORS[cat];
                const labels: Record<string, string> = {
                  streak: 'Streak', prayer: 'Prayer', habit: 'Habit', community: 'Community',
                };
                const icons: Record<string, string> = {
                  streak: 'FireIcon', prayer: 'SunIcon', habit: 'CheckCircleIcon', community: 'UsersIcon',
                };
                return (
                  <div key={cat} className={`p-3 rounded-xl border ${colors.bg} ${colors.border} flex items-center gap-3`}>
                    <Icon name={icons[cat] as Parameters<typeof Icon>[0]['name']} size={18} className={colors.text} />
                    <div>
                      <p className={`text-lg font-700 ${colors.text}`}>{count}</p>
                      <p className="text-xs text-muted-foreground">{labels[cat]}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
